import React, { useState } from 'react';
import { Switch } from '@rebass/forms';
import classNames from 'classnames';
import { Box, Button } from 'rebass';

import NiceModal from 'components/NiceModal';

/**
 * @typedef {{
 *     value: 0 | 1,
 *     action?: string
 * }} DoubleOptInSwitchValue
 */

/**
 * @typedef {Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> & { onChange: (value: DoubleOptInSwitchValue) => void }} DoubleOptInSwitchProps
 */

/**
 * Switch component with double opt-in confirmation dialogs.
 *
 * @param {DoubleOptInSwitchProps} props
 */
export function DoubleOptInSwitch({ onChange, disabled, checked, className, ...rest }) {
    const [isEnablingDialogOpen, setIsEnablingDialogOpen] = useState(false);
    const [isDisablingDialogOpen, setIsDisablingDialogOpen] = useState(false);

    const closeEnablingDialog = () => {
        setIsEnablingDialogOpen(false);
    };

    const closeDisablingDialog = () => {
        setIsDisablingDialogOpen(false);
    };

    const confirmEnablingDialog = () => {
        onChange({ value: 1 });
        closeEnablingDialog();
    };

    const confirmDisablingDialog = () => {
        onChange({ value: 0 });
        closeDisablingDialog();
    };

    return (
        <>
            <Switch
                className={classNames('switch-small', className)}
                disabled={disabled}
                checked={checked}
                onClick={() => (checked ? setIsDisablingDialogOpen(true) : setIsEnablingDialogOpen(true))}
                {...rest}
            />
            <NiceModal
                title="Turn on double opt-in?"
                isOpen={isEnablingDialogOpen}
                shouldCloseOnOverlayClick
                onRequestClose={closeEnablingDialog}
            >
                <p className="mb-6 color-text-secondary">Are you sure you want to turn on double opt-in?</p>
                <Box className="modal-actions">
                    <Button type="button" mr={2} onClick={closeEnablingDialog} variant="secondary-gray">
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        mr={0}
                        className="modal-primary"
                        onClick={confirmEnablingDialog}
                    >
                        Turn on
                    </Button>
                </Box>
            </NiceModal>
            <NiceModal
                title="Turn off double opt-in?"
                isOpen={isDisablingDialogOpen}
                shouldCloseOnOverlayClick
                onRequestClose={closeDisablingDialog}
            >
                <p className="mb-6 color-text-secondary">Are you sure you want to turn off double opt-in?</p>
                <Box className="modal-actions">
                    <Button type="button" mr={2} onClick={closeDisablingDialog} variant="secondary-gray">
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        mr={0}
                        className="modal-primary"
                        onClick={confirmDisablingDialog}
                    >
                        Turn off
                    </Button>
                </Box>
            </NiceModal>
        </>
    );
}
